import { Col, Row, Spin } from "antd";
import moment from "moment";
import React, { useMemo } from "react";
import { useIsMutating, useMutation, useQuery } from "react-query";
import { useLocation } from "react-router-dom";
import { autoOasisLogin, speedMateLogin } from "../../apis/Login/apis";
import { completeService, scompleteService } from "../../apis/Order";
import {
  fetchReservationDetail,
  sfetchManagerReservationDetail,
  sfetchReservationDetail,
} from "../../apis/ReservationDetail/apis";
import Button from "../../components/atoms/Button";
import { sendSignOut } from "../../components/organisms/Layout/apis";
import { ConsumerInfo } from "../../components/organisms/Reservation/Detail/ConsumerInfo";
import { DeliveryInfo } from "../../components/organisms/Reservation/Detail/DeliveryInfo";
import { OnSiteInfoForm } from "../../components/organisms/Reservation/Detail/OnSite";
import { OrderInfo } from "../../components/organisms/Reservation/Detail/OrderInfo";
import { ConfirmEm } from "../../components/organisms/Reservation/index.styled";
import useModals from "../../hooks/useModals";
import useShop from "../../hooks/useShop";
import useUser from "../../hooks/useUsers";
import { HttpError } from "../../network/types";
import {
  autoOasisCodeString,
  speedMateCodeString,
} from "../../routes/constants/path";
import sharedConstants from "../../shared/constants";
import { PartnerBosRepairStatuses } from "../../shared/enums";
import { alertError, alertSuccess } from "../../shared/utils";
import { getDecodeToken } from "../../shared/utils/Token";

const ReservationDetailPage: React.FC = () => {
  const location = useLocation();
  const isMutating = useIsMutating();

  const { openAlertModal, openConfirmModal, closeModal } = useModals();

  const splitPathname = location.pathname.split("/");
  const id = location.pathname.split("/")[splitPathname.length - 1];

  const specialCode: string | undefined =
    location.search?.includes(speedMateCodeString) ||
    location.search?.includes(autoOasisCodeString)
      ? location
        ? location?.search
            ?.split("&")
            ?.find(
              (text) =>
                text.includes(speedMateCodeString) ||
                text.includes(autoOasisCodeString)
            )
            ?.split("=")[1]
        : undefined
      : undefined;

  const { shop } = useShop();
  const { signIn, signOut, getUserID, getAdminInfo } = useUser();
  const singInSpeedMateMutation = useMutation(speedMateLogin, {
    onSuccess: (res) => {
      if (res.accessToken) {
        signIn(res.admin, res.accessToken);
      }
      window.location.reload();
    },
    onError: () => {},
  });

  const singInAutoOasisMutation = useMutation(autoOasisLogin, {
    onSuccess: (res) => {
      if (res.accessToken) {
        signIn(res.admin, res.accessToken);
      }
      window.location.reload();
    },
    onError: () => {},
  });

  /**
   * 예약 상세
   */
  const { data, refetch, remove, isLoading, isFetching } = useQuery(
    ["ReservationDetail", id],
    () =>
      specialCode
        ? sharedConstants.masterCodes.includes(specialCode)
          ? sfetchManagerReservationDetail(id)
          : sfetchReservationDetail(id)
        : fetchReservationDetail(id),
    {
      refetchInterval: 1000 * 60 * 3,
      onError: (err: HttpError) => {
        if ((err.status === 401 || err.status === 403) && specialCode) {
          signOut();
          signOutMutation.mutate();
        }
        alertError(err.message);
      },
    }
  );

  const signOutMutation = useMutation(sendSignOut, {
    onSuccess: () => {
      if (specialCode) {
        if (location.search?.includes(speedMateCodeString))
          singInSpeedMateMutation.mutate({ repairShopCode: specialCode });
        if (location.search?.includes(autoOasisCodeString))
          singInAutoOasisMutation.mutate({ autooasisCode: specialCode });
      }
    },
    onError: () => {
      if (specialCode) {
        if (location.search?.includes(speedMateCodeString))
          singInSpeedMateMutation.mutate({ repairShopCode: specialCode });
        if (location.search?.includes(autoOasisCodeString))
          singInAutoOasisMutation.mutate({ autooasisCode: specialCode });
      }
    },
  });

  /**
   * 작업완료 요청
   */
  const { mutateAsync: completeServiceMutateAsync } = useMutation(
    specialCode ? scompleteService : completeService,
    {
      onSuccess: () => {
        alertSuccess("작업완료되었습니다.");

        closeModal();
      },
      onError: (err: HttpError) => {
        closeModal();
        if ((err.status === 401 || err.status === 403) && specialCode) {
          signOut();
          signOutMutation.mutate();
        }
        if (
          err.notTranslate ===
          "CANNOT_COMPLETED_SERVICE_UNTIL_ALL_COMPLETED_GOODS_ISSUE"
        )
          openAlertModal({
            contents: (
              <>
                주문상품이 모두 <ConfirmEm>&lsquo;출고완료&rsquo;</ConfirmEm>
                상태가 되어야 <ConfirmEm>&lsquo;작업 완료&lsquo;</ConfirmEm>가
                가능합니다
              </>
            ),
            title: "작업완료 불가",
            okText: "닫기",
          });
        else alertError(err.message);
      },
    }
  );

  const onConfrimPurchase = () => {
    if (data?.repair.repairNo)
      openConfirmModal({
        contents: "해당 항목을 '작업완료'하시겠습니까?",
        title: "작업완료",
        onOk: () => {
          completeServiceMutateAsync(data?.repair.repairNo);
        },
        okText: "작업완료",
      });
  };

  /**
   * 출장매장 여부
   * @notice 임시
   */
  const isOnsite = useMemo(() => {
    return getAdminInfo()?.repairShopType === "출장교체";
  }, []);

  const master = useMemo(() => {
    const info = getDecodeToken();

    return (info?.isMaster && +shop === getUserID()) || false;
  }, [shop]);

  return (
    <Spin spinning={isLoading || isFetching || !!isMutating}>
      <Row gutter={[0, 24]} style={{ width: "100%" }}>
        {isOnsite && data && (
          <Col span={24}>
            <div>
              <OnSiteInfoForm
                dataForm={data}
                isMaster={
                  master ||
                  !!(
                    specialCode &&
                    sharedConstants.masterCodes.includes(specialCode)
                  )
                }
                onChange={refetch}
              />
            </div>
          </Col>
        )}
        {!isOnsite && (
          <Col span={24}>
            <div>
              <OrderInfo
                dataForm={data}
                isMaster={
                  master ||
                  !!(
                    specialCode &&
                    sharedConstants.masterCodes.includes(specialCode)
                  )
                }
              />
            </div>
          </Col>
        )}
        <Col span={24}>
          <div>
            {data && (
              <ConsumerInfo
                dataForm={data}
                isMaster={
                  master ||
                  !!(
                    specialCode &&
                    sharedConstants.masterCodes.includes(specialCode)
                  )
                }
              />
            )}
          </div>
        </Col>

        <Col span={24}>
          <DeliveryInfo dataForm={data} />
        </Col>
        <Col
          style={{ display: "flex", width: "100%", justifyContent: "flex-end" }}
          span={24}
        >
          <Button
            disabled={
              data?.repair.repairStatus !==
                PartnerBosRepairStatuses.RESERVATION_COMPLETED ||
              moment().isBefore(moment(data?.repair.reservedAt))
            }
            onClick={onConfrimPurchase}
          >
            작업 완료
          </Button>
        </Col>
      </Row>
    </Spin>
  );
};
export default ReservationDetailPage;
